import { CsvExportButton, useCsvExport } from '../components/CsvExport';
// Tasks per mockups/04-tasks.html: urgency buckets (Overdue · Today ·
// Tomorrow · This week · Later · No due date), Open/This week/Done/All tabs,
// project + priority filters with chips, quick-add rows, inline editing on
// every row, and the new-task modal (09-new-task, 520px).

import { useViewState } from '../lib/useViewState';
import { oneOf, str, nullableStr, intMin } from '../lib/viewState';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { melbourneToday } from '../../shared/dates';
import { addDaysIso, BUCKET_LABELS } from '../../shared/taskBuckets';
import type { ProjectWithClient, TaskRow as TaskRowType } from '../../shared/types';
import { RecurringTasksModal } from '../components/RecurringTasksModal';
import { QuickAddRow, TaskRow, type ProjectOption, type TaskRowData } from '../components/TaskRow';
import { TaskPanel } from '../components/TaskPanel';
import {
  BulkActionBar,
  FilterBar,
  formatDate,
  formatDayMonth,
  formatProjectNumber,
  Modal,
  PageHeader,
  SearchBox,
  Seg,
  SelectField,
  SkeletonRows,
  SortButton,
  Tabs,
} from '../components/primitives';
import { ViewChips } from '../components/ViewChips';
import { useBulkSelect } from '../lib/useBulkSelect';
import { useListKeyboard } from '../lib/listKeyboard';
import { reportError } from '../lib/errorEvents';

import { useIsMobile } from '../lib/useIsMobile';
import { TASK_SORT_OPTIONS, readTaskSort, saveTaskSort } from '../lib/taskSort';
import { taskList, type TaskView } from '../lib/taskFilter';
import { PageMenu } from '../components/PageMenu';
import { FilterSheet } from '../components/FilterSheet';
import { PickerSheet } from '../components/PickerSheet';
import { DateSheet, defaultDateChips } from '../components/DateSheet';
import { SearchIcon, FilterIcon, DotsIcon, SelectIcon, RepeatIcon, PlusIcon, XIcon } from '../components/icons';

interface TaskWithNames extends TaskRowType {
  project_name: string | null;
}

type View = TaskView;

const DONE_PAGE = 50;

// Below this the side panel would leave nothing for the list, so the notes
// open in the same Modal the other pages use (§7).
const PANEL_MIN_WIDTH = '(min-width: 761px)';

export const tasksViewCodec = {
  view: oneOf('view', ['open', 'week', 'done', 'all'], 'open'),
  query: str('q'), project: nullableStr('project'), priority: nullableStr('priority'),
  doneShown: intMin('shown', DONE_PAGE, DONE_PAGE),
};

export default function Tasks() {
  const { initial, publish } = useViewState(tasksViewCodec, state => {
    setView(state.view);
    setQuery(state.query);
    setFilters({ project: state.project, priority: state.priority });
    setDoneShown(state.doneShown);
  });
  const isMobile = useIsMobile();
  const [selectMode, setSelectMode] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [recurrenceCount, setRecurrenceCount] = useState<number | null>(null);
  const [draftFilters, setDraftFilters] = useState<Record<string, string | null>>({ project: null, priority: null });
  const [draftView, setDraftView] = useState<View>('open');
  const [draftQuery, setDraftQuery] = useState('');
  const [tasks, setTasks] = useState<TaskWithNames[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [view, setView] = useState<View>(() => initial.view);
  const [query, setQuery] = useState(() => initial.query);
  const [filters, setFilters] = useState<Record<string, string | null>>(() => ({ project: initial.project, priority: initial.priority }));
  const [sort, setSort] = useState(readTaskSort);
  useEffect(() => { saveTaskSort(sort); }, [sort]);
  const [showNew, setShowNew] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [moveProject, setMoveProject] = useState<string | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  // Task whose notes are open in the right-hand panel (wide screens only).
  const [notesTaskId, setNotesTaskId] = useState<string | null>(null);
  const [roomForPanel, setRoomForPanel] = useState(() => window.matchMedia(PANEL_MIN_WIDTH).matches);
  const [doneShown, setDoneShown] = useState(() => initial.doneShown);

  useEffect(() => {
    publish({ view, query, project: filters.project, priority: filters.priority, doneShown });
  }, [publish, view, query, filters.project, filters.priority, doneShown]);
  // Quiet loading state (Phase 1.3) — skeleton until the first fetch settles,
  // whether it succeeds or fails, instead of a flash of "0 open" / no rows.
  const [loaded, setLoaded] = useState(false);

  // Saved views (Phase 1.12) — same tab/filter/search state this page holds.
  const viewState = { view, query, ...filters };
  const isDefaultView = view === 'open' && !query && !filters.project && !filters.priority;
  const applyView = (p: Record<string, string>) => {
    setView(p.view === 'week' || p.view === 'done' || p.view === 'all' ? p.view : 'open');
    setQuery(p.query ?? '');
    setFilters({ project: p.project ?? null, priority: p.priority ?? null });
  };
  const today = melbourneToday();

  useEffect(() => {
    const mq = window.matchMedia(PANEL_MIN_WIDTH);
    const onChange = (e: MediaQueryListEvent) => {
      setRoomForPanel(e.matches);
      if (!e.matches) setNotesTaskId(null);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);


  // Every change PATCHes then refreshes, so two list fetches can be in
  // flight at once — only the newest response may land, or a slow one
  // overwrites a tick that was made after it was sent.
  const listSeq = useRef(0);
  const fetchTasks = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ tasks: TaskWithNames[] }>('/api/admin/tasks')
      .then((d) => {
        if (seq === listSeq.current) setTasks(d.tasks);
      })
      .catch(() => {})
      .finally(() => {
        if (seq === listSeq.current) setLoaded(true);
      });
  }, []);
  // The page's own mutations refresh at once, as they always have. Mutations
  // made elsewhere (the FAB quick-add sheet, Phase A build-note 4) arrive as
  // `ss:mutated` and are coalesced 250ms like Shell's badge refetch; an
  // immediate refresh cancels a pending one so a PATCH costs one fetch, not
  // two (api() fires the event before onChanged runs).
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchTasks();
  }, [fetchTasks]);
  useEffect(() => {
    fetchTasks();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchTasks, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    };
  }, [fetchTasks]);
  // The page menu's "N recurrences" sub-label: refetched on every open so
  // it tracks adds/deletes made in the recurring sheet (one small GET).
  useEffect(() => {
    if (!showMenu) return;
    let live = true;
    api<{ task_recurrences: unknown[] }>('/api/admin/task-recurrences')
      .then((d) => {
        if (live) setRecurrenceCount(d.task_recurrences.length);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [showMenu]);
  useEffect(() => {
    api<{ projects: ProjectWithClient[] }>('/api/admin/projects')
      .then((d) =>
        // Voided projects hold no tasks (the list route excludes them), so
        // they stay out of the picker too.
        setProjects(
          d.projects
            .filter((p) => p.voided_at === null)
            .map((p) => ({
              id: p.id, name: p.name, phase: p.phase,
              sub: [p.client_name, formatProjectNumber(p.project_number)].filter(Boolean).join(' · '),
              group: ['lead', 'pre-production', 'shoot', 'post', 'on_hold'].includes(p.phase) ? 'Active' : 'Recently delivered',
            })),
        ),
      )
      .catch(() => {});
  }, []);

  // Active (non-delivered/closed) projects first in pickers/filters.
  const pickerProjects = useMemo(() => {
    const activeRank = (p: ProjectOption) =>
      ['lead', 'pre-production', 'shoot', 'post', 'on_hold'].includes(p.phase) ? 0 : 1;
    return [...projects].sort((a, b) => activeRank(a) - activeRank(b) || a.name.localeCompare(b.name));
  }, [projects]);

  // The panel follows the row, so a task filtered out (or trashed) closes it.
  const notesTask = notesTaskId ? (tasks.find((t) => t.id === notesTaskId) ?? null) : null;
  useEffect(() => {
    if (notesTaskId && !notesTask) setNotesTaskId(null);
  }, [notesTaskId, notesTask]);
  useEffect(() => {
    if (!notesTask) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setNotesTaskId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notesTask]);

  const csvExport = useCsvExport('tasks', { view, query, ...filters }, {
    count: taskList(tasks, query, filters, view, today, Infinity).count,
    filterLabels: { project: projects.find(p => p.id === filters.project)?.name },
  });
  const openCountAll = tasks.filter((t) => t.status !== 'done').length;
  const { done, open, buckets, visibleBuckets, visibleTaskIds } = useMemo(
    () => taskList(tasks, query, filters, view, today, doneShown, sort),
    [tasks, query, filters, view, today, doneShown, sort],
  );
  // The filter sheet's "Show N tasks" — only worth computing while it's open.
  const draftCount = useMemo(
    () => (showFilters ? taskList(tasks, draftQuery, draftFilters, draftView, today, doneShown).count : 0),
    [showFilters, tasks, draftQuery, draftFilters, draftView, today, doneShown],
  );
  const projectItems = pickerProjects.map(p => ({ value: p.id, label: p.name, sub: p.sub, group: p.group }));

  const bulk = useBulkSelect(visibleTaskIds);
  const exitSelection = () => { bulk.clear(); setSelectMode(false); };
  const selectAll = () => visibleTaskIds.forEach(id => { if (!bulk.isSelected(id)) bulk.toggle(id); });
  const kb = useListKeyboard({
    ids: visibleTaskIds,
    // Off while any sheet or modal is up. Tasks has no per-row detail page,
    // so no onOpen — Enter is a no-op here.
    enabled: !showNew && !showMove && !showFilters && !showMenu && !showRecurring,
    onToggle: bulk.toggle,
    onClear: bulk.clear,
  });

  const runBulk = async (action: 'complete' | 'trash') => {
    const ids = bulk.ids;
    if (ids.length === 0) return;
    try {
      await api('/api/admin/tasks/bulk', { method: 'POST', json: { ids, action } });
    } catch (error) {
      reportError(error, 'Bulk task action failed.');
      return;
    }
    // A finished bulk action ends select mode on mobile too (review): an
    // empty selection would otherwise leave the header with a lone ×.
    exitSelection();
    refresh();
  };

  const runBulkMove = async (projectId: string) => {
    const ids = bulk.ids;
    if (ids.length === 0 || moveBusy) return;
    setMoveBusy(true);
    try {
      await api('/api/admin/tasks/bulk', {
        method: 'POST',
        json: { ids, action: 'move', project_id: projectId },
      });
    } catch (error) {
      reportError(error, 'Could not move the selected tasks.');
      return;
    } finally {
      setMoveBusy(false);
    }
    exitSelection();
    setShowMove(false);
    refresh();
  };

  const filterFields = [
    {
      key: 'project',
      label: 'Project',
      items: [
        ...pickerProjects.map((p) => ({ value: p.id, label: p.name })),
        { value: 'none', label: 'No project' },
      ],
      searchable: true,
    },
    { key: 'priority', label: 'Priority', items: [{ value: 'high', label: 'High ⚑' }] },
  ];

  const renderRow = (t: TaskWithNames) => (
    <div
      className={`bulk-row${bulk.count > 0 ? ' selactive' : ''}${kb.focusedId === t.id ? ' kbd-focus' : ''}`}
      key={t.id}
    >
      {!isMobile && <span
        className={`bulk-check${bulk.isSelected(t.id) ? ' on' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          bulk.toggle(t.id);
        }}
      />}
      <div className="bulk-row-item">
        <TaskRow
          task={t as TaskRowData}
          today={today}
          projects={pickerProjects}
          notesMode={!isMobile && roomForPanel ? 'panel' : 'modal'}
          selectable={isMobile && selectMode ? { selected: bulk.isSelected(t.id), onToggle: () => bulk.toggle(t.id) } : undefined}
          selected={notesTaskId === t.id}
          onOpenNotes={() => setNotesTaskId((id) => (id === t.id ? null : t.id))}
          onChanged={refresh}
        />
      </div>
    </div>
  );

  return (
    <>
      <PageHeader title="Tasks" count={`${openCountAll} open`}
        selection={isMobile && selectMode ? { count: bulk.count, onSelectAll: selectAll, onDone: exitSelection } : undefined}
        mobileActions={selectMode ? [{ key: 'done', label: 'Done', icon: <XIcon />, onPress: exitSelection }] : [
          { key: 'search', label: 'Search', icon: <SearchIcon />, onPress: () => window.dispatchEvent(new Event('ss:open-search')) },
          { key: 'filter', label: 'Filter', icon: <FilterIcon />, active: !isDefaultView, onPress: () => { setDraftFilters({ ...filters }); setDraftView(view); setDraftQuery(query); setShowFilters(true); } },
          { key: 'menu', label: 'More task actions', icon: <DotsIcon />, onPress: () => setShowMenu(true) },
        ]}
      />

      {isMobile ? <>
        <Tabs options={[
          { value: 'open', label: 'Open' }, { value: 'week', label: 'This week' },
          { value: 'done', label: 'Done' }, { value: 'all', label: 'All' },
        ]} value={view} onChange={setView} />
        {/* A saved view can carry a search; there is no SearchBox at this
            width, so the query shows as a chip that clears it (review). */}
        {query.trim() !== '' && (
          <div className="chiprow tasks-query-chip">
            <button type="button" className="c on" onClick={() => setQuery('')} aria-label={`Clear search “${query}”`}>
              “{query}” ×
            </button>
          </div>
        )}
        <div className="tasks-sort-mobile"><SortButton options={TASK_SORT_OPTIONS} value={sort} onChange={setSort} /></div>
      </> : <div className="toolbar tasks-toolbar">
        <Tabs
          options={[
            { value: 'open', label: 'Open' },
            { value: 'week', label: 'This week' },
            { value: 'done', label: 'Done' },
            { value: 'all', label: 'All' },
          ]}
          value={view}
          onChange={setView}
        />
        <FilterBar
          fields={filterFields}
          active={filters}
          onChange={(key, value) => setFilters((f) => ({ ...f, [key]: value }))}
        />
        <SortButton options={TASK_SORT_OPTIONS} value={sort} onChange={setSort} />
        <SearchBox placeholder="Search tasks…" value={query} onChange={setQuery} />
        <button className="btn" onClick={() => setShowRecurring(true)}>
          ↻ Recurring
        </button>
        <CsvExportButton state={csvExport} />
        <button className="btn primary" onClick={() => setShowNew(true)}>
          + New task
        </button>
      </div>}

      <ViewChips page="tasks" current={viewState} isDefault={isDefaultView} onApply={applyView} />

      <div className={`page-body${!isMobile && notesTask ? ' with-task-panel' : ''}`}>
        <div className="content wide" style={{ paddingTop: 4 }}>
          {!loaded ? (
            <SkeletonRows />
          ) : (
            <>
              {(view === 'open' || view === 'week' || view === 'all') && sort.key !== 'due' && (
                <div className="bucket">
                  <div className="group-head"><span className="group-title">{view === 'week' ? 'This week' : 'Open'}</span><span className="group-count">{open.length}</span></div>
                  {open.map(renderRow)}
                  <QuickAddRow hint="Add task…" defaults={{ due_date: view === 'week' ? today : null, project_id: filters.project && filters.project !== 'none' ? filters.project : null }} onAdded={refresh} />
                </div>
              )}
              {(view === 'open' || view === 'week' || view === 'all') && sort.key === 'due' &&
                visibleBuckets.map((bucket) => {
                  const list = buckets.get(bucket) ?? [];
                  if (list.length === 0 && bucket !== 'today' && bucket !== 'none') return null;
                  return (
                    <div className="bucket" key={bucket}>
                      <div className="group-head">
                        <span
                          className={`group-title${bucket === 'overdue' ? ' danger' : bucket === 'today' ? ' today' : ''}`}
                        >
                          {BUCKET_LABELS[bucket]}
                        </span>
                        <span className="group-count">{list.length}</span>
                      </div>
                      {list.map(renderRow)}
                      {bucket === 'today' && (
                        <QuickAddRow
                          hint="Add task for today…"
                          defaults={{ due_date: today, project_id: filters.project && filters.project !== 'none' ? filters.project : null }}
                          onAdded={refresh}
                        />
                      )}
                      {bucket === 'none' && view !== 'week' && (
                        <QuickAddRow
                          hint="Add task…"
                          defaults={{ project_id: filters.project && filters.project !== 'none' ? filters.project : null }}
                          onAdded={refresh}
                        />
                      )}
                    </div>
                  );
                })}

              {(view === 'done' || view === 'all') && (
                <div className="bucket">
                  <div className="group-head">
                    <span className="group-title">Done</span>
                    <span className="group-count">{done.length}</span>
                  </div>
                  {done.slice(0, doneShown).map(renderRow)}
                  {done.length > doneShown && (
                    <button className="show-more" onClick={() => setDoneShown((n) => n + DONE_PAGE)}>
                      Show {Math.min(DONE_PAGE, done.length - doneShown)} more →
                    </button>
                  )}
                  {done.length === 0 && <div className="empty-note">Nothing done yet.</div>}
                </div>
              )}
            </>
          )}
        </div>
        {!isMobile && notesTask && (
          <aside className="task-panel-col">
            <TaskPanel
              task={notesTask as TaskRowData}
              today={today}
              onChanged={refresh}
              onClose={() => setNotesTaskId(null)}
            />
          </aside>
        )}
      </div>

      <BulkActionBar
        count={bulk.count}
        actions={[
          { key: 'complete', label: '✓ Complete', onClick: () => runBulk('complete') },
          { key: 'move', label: 'Move…', onClick: () => { setMoveProject(null); setShowMove(true); } },
          { key: 'trash', label: 'Trash', danger: true, onClick: () => runBulk('trash') },
        ]}
        onClear={isMobile ? exitSelection : bulk.clear}
      />

      {showNew && (
        <NewTaskModal
          projects={pickerProjects}
          onClose={() => setShowNew(false)}
          onCreated={refresh}
        />
      )}
      {showMove && (isMobile ?
        <PickerSheet open title="Move to project" searchable items={projectItems}
          value={moveProject} onClose={() => setShowMove(false)} onSelect={setMoveProject}
          footer={<button type="button" className="cbtn wide pri" disabled={!moveProject || moveBusy}
            onClick={() => { if (moveProject) void runBulkMove(moveProject); }}>Move {bulk.count} tasks →</button>} /> :
        <MoveTasksModal
          projects={pickerProjects}
          count={bulk.count}
          onClose={() => setShowMove(false)}
          onMove={runBulkMove}
        />
      )}
      {isMobile && <>
        <FilterSheet open={showFilters} onClose={() => setShowFilters(false)}
          sections={[
            // A loose "All projects" row is the way back to no project filter
            // without clearing priority and Show as well (review).
            { key: 'project', label: 'Project', kind: 'picker', items: [{ value: '', label: 'All projects' }, ...projectItems, { value: 'none', label: 'No project' }], value: draftFilters.project ?? '', placeholder: 'All projects', onChange: project => setDraftFilters(f => ({ ...f, project: project || null })) },
            { key: 'priority', label: 'Priority', kind: 'chips', options: [{ value: 'any', label: 'Any' }, { value: 'high', label: 'High ⚑' }], value: draftFilters.priority ? 'high' : 'any', onChange: priority => setDraftFilters(f => ({ ...f, priority: priority === 'high' ? 'high' : null })) },
            { key: 'view', label: 'Show', kind: 'chips', options: [{ value: 'open', label: 'Open' }, { value: 'week', label: 'This week' }, { value: 'done', label: 'Done' }, { value: 'all', label: 'All' }], value: draftView, onChange: setDraftView },
          ]}
          resultLabel={`Show ${draftCount} tasks`}
          onClear={() => { setDraftFilters({ project: null, priority: null }); setDraftView('open'); setDraftQuery(''); }}
          onApply={() => { setFilters(draftFilters); setView(draftView); setQuery(draftQuery); setShowFilters(false); }} />
        <PageMenu open={showMenu} onClose={() => setShowMenu(false)} items={[
          { key: 'select', label: 'Select tasks', icon: <SelectIcon />, onSelect: () => setSelectMode(true) },
          { key: 'recurring', label: 'Recurring tasks', icon: <RepeatIcon />, sublabel: recurrenceCount === null ? undefined : `${recurrenceCount} recurrence${recurrenceCount === 1 ? '' : 's'} · generated daily`, onSelect: () => setShowRecurring(true) },
          { key: 'new', label: 'New task', icon: <PlusIcon />, onSelect: () => setShowNew(true) },
          csvExport.item,
        ]} />
      </>}
      {showRecurring && <RecurringTasksModal onClose={() => setShowRecurring(false)} />}
    </>
  );
}

// ── Move-tasks modal (bulk "move to project", Phase 1.13 completion) ────

function MoveTasksModal({
  projects,
  count,
  onClose,
  onMove,
}: {
  projects: ProjectOption[];
  count: number;
  onClose: () => void;
  onMove: (projectId: string) => Promise<void>;
}) {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!projectId || busy) return;
    setBusy(true);
    await onMove(projectId);
  };

  return (
    <Modal
      title="Move tasks"
      context="TASKS"
      width={420}
      onClose={onClose}
      hint={`${count} task${count === 1 ? '' : 's'} selected`}
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={submit} disabled={!projectId || busy}>
            Move →
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="f-field span-2">
          <span className="f-label">Project</span>
          <SelectField
            items={projects.map((p) => ({ value: p.id, label: p.name }))}
            value={projectId}
            onChange={setProjectId}
            placeholder="Pick a project…"
            searchable
          />
        </div>
      </div>
    </Modal>
  );
}

// ── New task modal (09-new-task, 520px) ──────────────────────────────────

export function NewTaskModal({
  projects,
  defaultProjectId = null,
  onClose,
  onCreated,
}: {
  projects: ProjectOption[];
  /** Pre-selects a project (Projects swipe "+ Task", Project detail "···" → New task). */
  defaultProjectId?: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const isMobile = useIsMobile();
  const [dateOpen, setDateOpen] = useState(false);
  const today = melbourneToday();
  const dueOptions = [
    { value: 'none', label: 'None' },
    { value: today, label: `Today · ${formatDate(today)}` },
    { value: addDaysIso(today, 1), label: `Tomorrow · ${formatDate(addDaysIso(today, 1))}` },
    { value: addDaysIso(today, 3), label: `+3 days · ${formatDate(addDaysIso(today, 3))}` },
    { value: addDaysIso(today, 7), label: `+1 week · ${formatDate(addDaysIso(today, 7))}` },
  ];

  // Due defaults to today (Henry, phase-5 review).
  const [form, setForm] = useState({
    title: '',
    project_id: defaultProjectId as string | null,
    due: today,
    priority: 'normal' as 'normal' | 'high',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!form.title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/admin/tasks', {
        method: 'POST',
        json: {
          title: form.title.trim(),
          project_id: form.project_id,
          due_date: form.due === 'none' ? null : form.due,
          priority: form.priority === 'high' ? 1 : 2,
        },
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New task"
      context="TASKS"
      width={520}
      onClose={onClose}
      hint="Tip: the + row on any list adds tasks faster"
      foot={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => submit()} disabled={!form.title.trim() || busy}>
            Create task →
          </button>
        </>
      }
    >
      <form className="form-grid" onSubmit={submit}>
        <div className="f-field span-2">
          <span className="f-label">
            Title <span className="req">*</span>
          </span>
          <input
            className="f-input"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            autoFocus
          />
        </div>
        <div className="f-field">
          <span className="f-label">Project</span>
          <SelectField
            label="Project"
            items={projects.map((p) => ({ value: p.id, label: p.name, ...(isMobile ? { sub: p.sub, group: p.group } : {}) }))}
            value={form.project_id}
            onChange={(project_id) => setForm((f) => ({ ...f, project_id }))}
            placeholder="No project"
            searchable
          />
        </div>
        <div className="f-field">
          <span className="f-label">Due</span>
          {isMobile ? <>
            <div className="chiprow tasks-due-chips">
              {[{ date: null, label: 'None' }, ...defaultDateChips(today),
                ...(!dueOptions.some(o => o.value === form.due) ? [{ date: form.due, label: formatDayMonth(form.due) }] : []),
              ].map(c => <button type="button" key={c.date ?? 'none'} className={`c${form.due === (c.date ?? 'none') ? ' on' : ''}`}
                aria-pressed={form.due === (c.date ?? 'none')} onClick={() => setForm(f => ({ ...f, due: c.date ?? 'none' }))}>{c.label}</button>)}
              <button type="button" className="c" onClick={() => setDateOpen(true)}>Pick…</button>
            </div>
            <DateSheet open={dateOpen} title="Due date" value={form.due === 'none' ? null : form.due}
              allowClear onClose={() => setDateOpen(false)} onPick={date => { setForm(f => ({ ...f, due: date ?? 'none' })); setDateOpen(false); }} />
          </> : <SelectField
            items={dueOptions}
            value={form.due}
            onChange={(due) => setForm((f) => ({ ...f, due }))}
            mono
          />}
        </div>
        <div className="f-field span-2">
          <span className="f-label">Priority</span>
          <div>
            <Seg
              options={[
                { value: 'normal', label: 'Normal' },
                { value: 'high', label: 'High ⚑' },
              ]}
              value={form.priority}
              onChange={(priority) => setForm((f) => ({ ...f, priority }))}
            />
          </div>
        </div>
        {error && <p className="error-text span-2">{error}</p>}
      </form>
    </Modal>
  );
}
